import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

// Interfaces para tipado
interface PaymentOptionForm {
  code: string;
  name: string;
  paymentTitle: string;
  paymentDescription: string;
  showClient: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentOptionsService {
  private baseUrl: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.BASE_LA;
  }

  /**
   * Obtener configuración de payment options para la aplicación
   * GET /api/app-config/configuration?admin=true
   */
  getPaymentOptions = async () => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/configuration?admin=true`;
    
    console.log('=== GET PAYMENT OPTIONS DEBUG ===');
    console.log('URL:', url);
    console.log('Headers:', headers);
    console.log('Base URL:', this.baseUrl);
    
    try {
      const response = await lastValueFrom(
        this.httpClient.get<any>(url, { headers })
      );
      
      console.log('Get payment options response:', response);
      console.log('Response structure:', {
        hasData: !!response?.data,
        hasPaymentOptions: !!response?.data?.paymentOptions,
        paymentOptionsCount: response?.data?.paymentOptions?.length || 0
      });
      
      return response;
    } catch (error: any) {
      console.error('Error getting payment options:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        statusText: error?.statusText,
        url: error?.url
      });
      throw error;
    }
  };

  /**
   * Crear nueva payment option
   * POST /api/app-config/payment-options
   */
  createPaymentOption = async (paymentOptionData: PaymentOptionForm) => {
    const headers = { 'Content-Type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/payment-options`;
    
    console.log('=== CREATE PAYMENT OPTION DEBUG ===');
    console.log('URL:', url);
    console.log('Body:', paymentOptionData);
    console.log('Headers:', headers);
    
    try {
      const response = await lastValueFrom(
        this.httpClient.post<any>(url, paymentOptionData, { headers })
      );
      
      console.log('Create payment option response:', response);
      return response;
    } catch (error: any) {
      console.error('Error creating payment option:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        statusText: error?.statusText,
        url: error?.url
      });
      throw error;
    }
  };

  /**
   * Actualizar payment option existente
   * PUT /api/app-config/payment-options/{id}
   */
  updatePaymentOption = async (id: string, paymentOptionData: PaymentOptionForm) => {
    const headers = { 'Content-Type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/payment-options/${id}`;
    
    console.log('=== UPDATE PAYMENT OPTION DEBUG ===');
    console.log('URL:', url);
    console.log('Body:', paymentOptionData);
    console.log('Headers:', headers);
    
    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(url, paymentOptionData, { headers })
      );
      
      console.log('Update payment option response:', response);
      return response;
    } catch (error: any) {
      console.error('Error updating payment option:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        statusText: error?.statusText,
        url: error?.url
      });
      throw error;
    }
  };

  /**
   * Eliminar payment option
   * DELETE /api/app-config/payment-options/{id}
   */
  deletePaymentOption = async (id: string) => {
    const headers = { 'Content-Type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/payment-options/${id}`;
    
    console.log('=== DELETE PAYMENT OPTION DEBUG ===');
    console.log('URL:', url);
    console.log('Headers:', headers);
    
    try {
      const response = await lastValueFrom(
        this.httpClient.delete<any>(url, { headers })
      );
      
      console.log('Delete payment option response:', response);
      return response;
    } catch (error: any) {
      console.error('Error deleting payment option:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        statusText: error?.statusText,
        url: error?.url
      });
      throw error;
    }
  };

  /**
   * Activar/Desactivar payment option
   * PUT /api/app-config/payment-options/{id}/toggle
   */
  togglePaymentOption = async (id: string, showClient: boolean) => {
    const headers = { 'Content-Type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/payment-options/${id}/toggle`;
    const body = { showClient };
    
    console.log('=== TOGGLE PAYMENT OPTION DEBUG ===');
    console.log('URL:', url);
    console.log('Body:', body);
    console.log('Headers:', headers);
    
    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(url, body, { headers })
      );
      
      console.log('Toggle response:', response);
      return response;
    } catch (error: any) {
      console.error('Error toggling payment option:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        statusText: error?.statusText,
        url: error?.url
      });
      throw error;
    }
  };

  /**
   * Migración inicial del sistema (uso interno)
   * POST /api/app-config/migrate
   */
  migratePaymentOptions = async () => {
    const headers = { 'Content-Type': 'application/json' };
    
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/app-config/migrate`,
          {},
          { headers }
        )
      );
    } catch (error) {
      console.error('Error migrating payment options:', error);
      throw error;
    }
  };

  /**
   * Obtener estadísticas de cuentas por payment option
   */
  getPaymentOptionStats = async (paymentOptionId: string) => {
    const headers = { 'content-type': 'application/json' };
    
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/app-config/payment-options/${paymentOptionId}/stats`,
          { headers }
        )
      );
    } catch (error) {
      console.error('Error getting payment option stats:', error);
      return { accountCount: 0, activeCount: 0 };
    }
  };

  /**
   * Cambiar prioridad individual de un payment option
   * PUT /api/app-config/payment-options/{id}/priority
   */
  updatePaymentOptionPriority = async (id: string, priority: number) => {
    const headers = { 'Content-Type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/payment-options/${id}/priority`;
    const body = { priority };
    
    console.log('=== UPDATE PAYMENT OPTION PRIORITY DEBUG ===');
    console.log('URL:', url);
    console.log('Body:', body);
    console.log('Headers:', headers);
    
    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(url, body, { headers })
      );
      
      console.log('Update priority response:', response);
      return response;
    } catch (error: any) {
      console.error('Error updating payment option priority:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        statusText: error?.statusText,
        url: error?.url
      });
      throw error;
    }
  };

  /**
   * Reordenar múltiples payment options
   * PUT /api/app-config/payment-options/reorder
   */
  reorderPaymentOptions = async (reorderData: Array<{ id: string; priority: number }>) => {
    const headers = { 'Content-Type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/payment-options/reorder`;
    const body = { reorderData };
    
    console.log('=== REORDER PAYMENT OPTIONS DEBUG ===');
    console.log('URL:', url);
    console.log('Body:', body);
    console.log('Headers:', headers);
    
    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(url, body, { headers })
      );
      
      console.log('Reorder response:', response);
      return response;
    } catch (error: any) {
      console.error('Error reordering payment options:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        statusText: error?.statusText,
        url: error?.url
      });
      throw error;
    }
  };
} 